import * as React from 'react';
import HeaderMenu from 'components/Menu/HeaderMenu';
import SideMenu from 'components/Menu/SideMenu';
import { Tab } from 'components/Menu/SideMenu/SideMenu';
import { createUseStyles } from 'react-jss';
import { useRouter } from 'found';
import { createFragmentContainer, graphql } from 'react-relay';

import type { AppHolder_manager } from './__generated__/AppHolder_manager.graphql';
import ManagerModal from 'components/Manager/ManagerModal';
import CompanyModal from 'components/Manager/CompanyModal';
import { Logout } from 'helpers/logout';
import { useBookingModalDispatch } from './BookingModalProvider';
import Icon from 'sharedComponents/core/Icon';
import logoSectionCheckerboard from 'assets/logo/logo-section-checkerboard.svg';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTableCells,
  faUsers,
  faFileCheck,
  faUserCheck,
  faShieldCheck,
  faScrewdriverWrench
} from '@fortawesome/pro-regular-svg-icons';

type Props = {
  children?: React.ReactChildren;
  manager: AppHolder_manager;
  retry: () => void;
};

const useStyles = createUseStyles(() => ({
  appHolder: {
    display: 'flex',
    padding: '0px'
  },
  gridLayout: {
    display: 'grid',
    gridTemplateColumns: '256px 1fr',
    gridTemplateRows: '65px 1fr',
    height: '100vh'
  },
  holder: {
    overflow: 'scroll'
  },
  logoSection: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    padding: '0px 21px',
    height: 65,
    borderBottom: '1px solid #EDEDED',
    borderRight: '1px solid #EDEDED',
    background: `
      url(${logoSectionCheckerboard}) left top / auto 100% no-repeat,
      white
    `
  }
}));

const AppHolder = ({ children, manager, retry }: Props) => {
  const classes = useStyles();
  const { match, router } = useRouter();

  const securityProgrammesCount =
    manager?.company?.securityProgrammes?.pageInfo?.total ?? 0;
  const machineReportsCount =
    manager?.company?.machineReports?.pageInfo?.total ?? 0;

  // Define all tab configurations
  const tabConfigs = [
    {
      iconComponent: <FontAwesomeIcon icon={faTableCells} />,
      iconComponentSelected: <FontAwesomeIcon icon={faTableCells} />,
      name: 'Dashboard',
      path: '/app',
      routes: ['/']
    },
    {
      iconComponent: <FontAwesomeIcon icon={faUsers} />,
      iconComponentSelected: <FontAwesomeIcon icon={faUsers} />,
      name: 'Delegates',
      path: '/app/delegates',
      routes: ['/delegates', '/delegates/:uuid']
    }
  ];

  const companyTabConfigs = [
    {
      show: true,
      iconComponent: <FontAwesomeIcon icon={faFileCheck} />,
      iconComponentSelected: <FontAwesomeIcon icon={faFileCheck} />,
      name: 'Audits',
      path: '/app/audits',
      routes: ['/audits']
    },
    {
      show: manager.company?.machineReports && machineReportsCount > 0,
      iconComponent: <FontAwesomeIcon icon={faScrewdriverWrench} />,
      name: 'Machine Reports',
      path: '/app/machine-reports',
      routes: ['/machine-reports']
    },
    {
      show: true,
      iconComponent: <FontAwesomeIcon icon={faUserCheck} />,
      iconComponentSelected: <FontAwesomeIcon icon={faUserCheck} />,
      name: 'Background Checks',
      path: '/app/background-checks',
      routes: ['/background-checks', '/background-checks/:uuid']
    },
    {
      show: manager.company?.securityProgrammes && securityProgrammesCount > 0,
      iconComponent: <FontAwesomeIcon icon={faShieldCheck} />,
      iconComponentSelected: <FontAwesomeIcon icon={faShieldCheck} />,
      name: 'Security Programmes',
      path: '/app/security-programmes',
      routes: ['/security-programmes']
    }
  ].filter((tab) => tab.show);

  const allTabConfigs = [...tabConfigs, ...companyTabConfigs];

  // Build tabs with IDs
  const tabs: Tab[] = tabConfigs.map((tab, index) => ({
    id: index,
    iconComponent: tab.iconComponent,
    iconComponentSelected: tab.iconComponentSelected,
    name: tab.name
  }));

  const companyTabs: Tab[] = companyTabConfigs.map((tab, index) => ({
    id: tabs.length + index,
    iconComponent: tab.iconComponent,
    iconComponentSelected: tab.iconComponentSelected,
    name: tab.name
  }));

  // Combine all tabs to be used for selection
  const allTabs = [...tabs, ...companyTabs];

  // Build route to tab ID mapping
  const routeToTabId: { [route: string]: number } = {};
  allTabConfigs.forEach((tab, index) => {
    tab.routes.forEach((route) => {
      routeToTabId[route] = index;
    });
  });

  const [openManagerModal, setOpenManagerModal] = React.useState(false);
  const [openCompanyModal, setOpenCompanyModal] = React.useState(false);
  const dispatchModal = useBookingModalDispatch();

  if (manager.uuid === '00000000-0000-0000-0000-000000000000') {
    Logout(
      () => {},
      () => {}
    );
  }

  const selected = () => {
    const { routes } = match;
    const currentRouter = routes[routes.length - 1];
    // Default to root if path is undefined
    const currentPath = currentRouter.path || '/';
    return allTabs[routeToTabId[currentPath] || 0];
  };

  if (match.location.query.course) {
    console.log('open course', match.location.query.course);

    try {
      var courseID = parseInt(match.location.query.course);
      // Get course info
      dispatchModal({
        type: 'show',
        courseId: courseID
      });
    } catch (err) {
      console.warn(err);
    }
  }

  return (
    <div>
      <div className={classes.gridLayout}>
        <div className={classes.logoSection}>
          <Icon name="TTC_Logo" size={90} />
        </div>
        <HeaderMenu
          manager={manager}
          onEditDetails={() => setOpenManagerModal(true)}
          onEditCompany={() => setOpenCompanyModal(true)}
        />
        <SideMenu
          manager={manager}
          onEditDetails={() => setOpenManagerModal(true)}
          onEditCompany={() => setOpenCompanyModal(true)}
          tabs={tabs}
          companyTabs={manager.hasComplianceAccess ? companyTabs : []}
          selected={selected()}
          onClick={(tab) => {
            const route = allTabConfigs[tab.id]?.path;
            if (route) {
              router.push(route);
            }
          }}
        />

        <div className={classes.holder}>
          <div className={classes.appHolder}>{children}</div>
        </div>
      </div>
      <ManagerModal
        isOpen={openManagerModal}
        onClose={() => setOpenManagerModal(false)}
        refetch={retry}
        manager={manager}
      />
      {manager.company && (
        <CompanyModal
          isOpen={openCompanyModal}
          onClose={() => setOpenCompanyModal(false)}
          refetch={retry}
          company={manager.company}
        />
      )}
    </div>
  );
};

export default createFragmentContainer(AppHolder, {
  manager: graphql`
    fragment AppHolder_manager on Manager {
      uuid
      firstName
      lastName
      profileImageUrl
      hasComplianceAccess
      company {
        logoURL
        name
        securityProgrammes(page: { offset: 0, limit: 0 }) {
          pageInfo {
            total
          }
        }
        machineReports(page: { offset: 0, limit: 0 }, filter: { sent: true }) {
          pageInfo {
            total
          }
        }
        ...CompanyModal_company
      }
      ...ManagerModal_manager
      ...HeaderMenu_manager
    }
  `
});
